/**
 * Externally-enforced wall-clock timeout for a single async step.
 *
 * Why this exists instead of a bare `AbortController` + `signal`: an abort is a
 * *request* the callee may ignore. A Cloudflare service binding whose target
 * container is wedged can leave `fetch()` pending long after `controller.abort()`
 * fires, so a signal-only cap silently doesn't cap anything — the caller still
 * hangs until Cloudflare sweeps the invocation ~30min later. `withTimeout`
 * therefore does BOTH: it aborts (so a well-behaved callee unwinds and frees its
 * subrequest) *and* races the timer, so the caller is guaranteed to regain
 * control at `timeoutMs` regardless of whether the abort was honored.
 *
 * Incident 2026-07-18: `social-render-pending` was auto-swept by the stale-run
 * sweeper ("still 'running' after 30min") while its own render carried a 6-min
 * AbortController cap — the abort never took, so the 6-min "cap" bounded nothing
 * and the whole cron run was swept instead of failing cleanly.
 *
 * The losing promise is NOT cancelled (JS promises aren't cancellable); its work
 * is orphaned and its result discarded. Only use this where orphaning is safe —
 * i.e. the step is idempotent or its side effects are re-derivable.
 */

/** Thrown when a `withTimeout` step exceeds its budget. Detect via `isTimeoutError`. */
export class TimeoutError extends Error {
	readonly label: string;
	readonly timeoutMs: number;

	constructor(label: string, timeoutMs: number) {
		super(`${label} timed out after ${timeoutMs}ms`);
		this.name = "TimeoutError";
		this.label = label;
		this.timeoutMs = timeoutMs;
	}
}

/**
 * Name-based check (not `instanceof`) so a TimeoutError that crossed a module
 * or worker boundary — or was re-thrown as a plain Error with the same name —
 * is still recognised.
 */
export function isTimeoutError(err: unknown): err is TimeoutError {
	return err instanceof Error && err.name === "TimeoutError";
}

/**
 * Run `fn` with a hard wall-clock cap. `fn` receives an `AbortSignal` that is
 * aborted when the budget expires — pass it to `fetch` so a cooperative callee
 * unwinds early; the race guarantees the caller returns even when it doesn't.
 *
 * Rejects with `TimeoutError` on expiry, otherwise resolves/rejects as `fn` does.
 */
export async function withTimeout<T>(
	label: string,
	timeoutMs: number,
	fn: (signal: AbortSignal) => Promise<T>,
): Promise<T> {
	const controller = new AbortController();
	let timer: ReturnType<typeof setTimeout> | undefined;
	let expired = false;
	const expiry = new Promise<never>((_resolve, reject) => {
		timer = setTimeout(() => {
			expired = true;
			// Abort first so a cooperative callee stops burning a subrequest, then
			// reject so the caller regains control even if it doesn't.
			controller.abort();
			reject(new TimeoutError(label, timeoutMs));
		}, timeoutMs);
	});
	// `expiry` must own a rejection handler from the moment it exists, not only
	// once `Promise.race` attaches one. `fn` is typed to return a promise but
	// nothing stops a non-async callback from throwing SYNCHRONOUSLY — that exit
	// path never reaches the race, so without this the timer would later reject an
	// unwatched promise and crash the invocation with an unhandled rejection.
	expiry.catch(() => {});
	try {
		// `fn(...)` is invoked INSIDE the try so a synchronous throw still runs the
		// `finally` and clears the timer. Before this, a sync throw leaked the timer
		// and held a Cloudflare `scheduled()` invocation open for the full budget.
		//
		// A COOPERATIVE callee (fetch honoring the signal) rejects with AbortError the
		// instant we abort — and wins the race against `expiry`. Without this
		// normalization the caller would see an AbortError on the common path and a
		// TimeoutError only on the wedged path, so a single `isTimeoutError` check
		// would silently miss most real timeouts. Both paths must look identical.
		const work = fn(controller.signal).catch((err: unknown) => {
			if (expired) throw new TimeoutError(label, timeoutMs);
			throw err;
		});
		return await Promise.race([work, expiry]);
	} finally {
		clearTimeout(timer);
	}
}
